// Aggregator that walks a 1 Hz telemetry array once and returns a
// per-session + per-device summary block. Persisted alongside the raw
// array as `telemetry_summary` so the session list view can render
// avg/max tiles without re-iterating the full array.
//
// Row shape (see useTelemetryRecorder for the contract):
//   { t,         seconds since session start
//     dev?,      "indoor_bike" | "rower" | null/undefined when no FTMS active
//     w?,        power_watts (rounded)
//     hr?,       heart-rate bpm
//     c?,        cadence_rpm (bike)
//     sp?,       speed_kmh × 10 (bike)
//     spm?,      stroke_rate_spm (rower)
//     p?,        pace_sec_per_500m (rower)
//     d?,        total_distance_m
//     e?,        total_energy_kcal (the machine's own count)
//     sc?,       stroke_count (rower)
//     r?,        resistance_level
//     air?       1 on an air bike (utils/airBike), left out of VO2max
//   }

import { airBikeSteady, estimateVo2max } from "../vo2max";
import { machineEfficiency } from "./efficiency";
import { zoneSummary } from "../heartRateZones";
import { counterTotal, machineEnergyByDevice } from "./energy";

const emptyStats = () => ({ sum: 0, count: 0, min: Infinity, max: -Infinity });

const push = (s, v) => {
  if (v == null) return;
  s.sum += v;
  s.count += 1;
  if (v < s.min) s.min = v;
  if (v > s.max) s.max = v;
};

const finishStats = (s, round = 0) => {
  if (s.count === 0) return null;
  const factor = Math.pow(10, round);
  return {
    avg: Math.round((s.sum / s.count) * factor) / factor,
    min: s.min,
    max: s.max,
    count: s.count,
  };
};

const newDeviceBucket = (type) => ({
  type,
  duration_s: 0,
  power: emptyStats(),
  cadence: emptyStats(),
  speed_kmh_x10: emptyStats(),
  stroke_rate: emptyStats(),
  pace: emptyStats(),
  resistance: emptyStats(),
  distances: [],
  strokes: [],
});

const finishDeviceBucket = (b) => {
  if (b.duration_s === 0) return null;
  const out = { type: b.type, duration_s: b.duration_s };
  const power = finishStats(b.power);
  if (power) out.power = power;
  if (b.type === "indoor_bike") {
    const cadence = finishStats(b.cadence, 1);
    const speed = finishStats(b.speed_kmh_x10, 1);
    if (cadence) out.cadence_rpm = cadence;
    if (speed) {
      out.speed_kmh = {
        avg: Math.round((speed.avg / 10) * 10) / 10,
        min: Math.round((speed.min / 10) * 10) / 10,
        max: Math.round((speed.max / 10) * 10) / 10,
        count: speed.count,
      };
    }
  }
  if (b.type === "rower") {
    const sr = finishStats(b.stroke_rate, 1);
    const pace = finishStats(b.pace);
    if (sr) out.stroke_rate_spm = sr;
    if (pace) {
      // lower pace = faster, so report best as min.
      out.pace_sec_per_500m = {
        avg: pace.avg,
        best: pace.min,
        worst: pace.max,
        count: pace.count,
      };
    }
  }
  const resistance = finishStats(b.resistance, 1);
  if (resistance) out.resistance = resistance;
  // Running totals, kept across a machine that restarts its count mid-session.
  if (b.distances.length > 0) out.total_distance_m = counterTotal(b.distances);
  if (b.strokes.length > 0) out.total_strokes = counterTotal(b.strokes);
  return out;
};

/**
 * `hrBasis` (utils/heartRateZones.js heartRateBasis) adds time in each
 * heart-rate zone to the hr block (#94).
 */
export function summariseTelemetry(samples, { hrBasis = null } = {}) {
  if (!Array.isArray(samples) || samples.length === 0) {
    return {
      duration_s: 0,
      sample_count: 0,
      hr: null,
      devices: {},
      no_device_s: 0,
    };
  }

  const hr = emptyStats();
  const hrValues = [];
  let hrPresent = 0;
  const devices = {}; // type → bucket
  let noDeviceS = 0;

  for (const row of samples) {
    const dev = row.dev || null;
    if (dev) {
      const bucket = devices[dev] || (devices[dev] = newDeviceBucket(dev));
      bucket.duration_s += 1;
      push(bucket.power, row.w);
      push(bucket.cadence, row.c);
      push(bucket.speed_kmh_x10, row.sp);
      push(bucket.stroke_rate, row.spm);
      push(bucket.pace, row.p);
      push(bucket.resistance, row.r);
      if (row.d != null) bucket.distances.push(row.d);
      if (row.sc != null) bucket.strokes.push(row.sc);
    } else {
      noDeviceS += 1;
    }
    if (row.hr != null) {
      push(hr, row.hr);
      hrValues.push(row.hr);
      hrPresent += 1;
    }
  }

  const last = samples[samples.length - 1];
  const duration_s = (last?.t ?? samples.length - 1) + 1;
  const sample_count = samples.length;

  const finishedDevices = {};
  const energy = machineEnergyByDevice(samples);
  for (const [type, bucket] of Object.entries(devices)) {
    const finished = finishDeviceBucket(bucket);
    if (!finished) continue;
    // Calories: the machine's own count, or from the work done (energy.js).
    if (energy[type]) {
      finished.energy_kcal = energy[type].kcal;
      finished.energy_source = energy[type].source;
    }
    // Watts per beat, aerobic drift and metres per stroke (efficiency.js).
    finishedDevices[type] = { ...finished, ...(machineEfficiency(samples, type) || {}) };
  }

  const hrStats = finishStats(hr);
  const hrBlock = hrStats
    ? {
        avg: hrStats.avg,
        min: hrStats.min,
        max: hrStats.max,
        present_pct: Math.round((hrPresent / sample_count) * 100),
        // One row a second, so each heart-rate reading is one second.
        ...(zoneSummary(hrValues, hrBasis, 1) ? { zones: zoneSummary(hrValues, hrBasis, 1) } : {}),
      }
    : null;

  // VO2max from a steady stretch on a bike (#194), when there was one.
  const vo2max = estimateVo2max(samples, hrBasis);
  // An air bike's steady stretch, for calibrating it to your rowing VO2max.
  const airSteady = airBikeSteady(samples, hrBasis);

  return {
    duration_s,
    sample_count,
    hr: hrBlock,
    devices: finishedDevices,
    no_device_s: noDeviceS,
    ...(vo2max ? { vo2max } : {}),
    ...(airSteady ? { air_steady: airSteady } : {}),
  };
}

// Whether a session has anything the Telemetry chart can draw: heart rate the
// recorder sampled, or a paired machine. Every Coach session saves a summary,
// strap or not, and its `hr` can be the Timer's own average (source "device")
// rather than samples, so neither the summary nor its `hr` alone means there
// is a chart to show.
export function hasChartableTelemetry(session) {
  const samples = session?.telemetry;
  if (Array.isArray(samples) && samples.length > 0) {
    return samples.some(
      (r) => r.hr != null || r.w != null || r.c != null || r.spm != null || r.dev,
    );
  }
  const summary = session?.telemetry_summary;
  if (!summary) return false;
  if (summary.hr && summary.hr.source !== "device") return true;
  return Object.keys(summary.devices || {}).length > 0;
}
